import { Chip, Grid, Link, Typography, useTheme } from '@mui/material';
import {
  Cancel,
  CheckCircle,
  NotificationsActive,
  WarningRounded,
  WatchLater,
} from '@mui/icons-material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings/useNotificationsSettings';
import {
  openNativeAppNotificationSettings,
  requestCriticalAlertsPermission,
} from 'shared/utils';

const StatusIndicator = (props: { isEnabled: boolean }): JSX.Element => {
  const { isEnabled } = props;
  const { t } = useTranslation();

  const iconSx = { height: 14, width: 14 };
  const chipSx = {
    '& .MuiChip-label': { paddingLeft: 1.5, marginBottom: 0.25 },
  };

  if (isEnabled) {
    return (
      <Chip
        color="success"
        icon={<CheckCircle color="success" fontSize="small" sx={iconSx} />}
        label={t('notifications.delivery.enabled')}
        sx={chipSx}
        variant="watchduty"
      />
    );
  }

  return (
    <Chip
      color="error"
      icon={<Cancel color="error" fontSize="small" sx={iconSx} />}
      label={t('notifications.delivery.disabled')}
      sx={chipSx}
      variant="watchduty"
    />
  );
};

const DisabledContent = (props: { notificationType: string }): JSX.Element => {
  const { notificationType } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <Grid offset={1} size={11}>
        <Typography variant="body2">
          {t(`notifications.delivery.${notificationType}.details`)}
        </Typography>
      </Grid>
      <Grid offset={1} size={11}>
        {/* the linter doesn't know component="button" renders a button under the hood */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          color={theme.palette.text.primary}
          component="button"
          fontWeight="fontWeightBold"
          variant="body2"
          onClick={openNativeAppNotificationSettings}
        >
          {t('notifications.delivery.enable')}
        </Link>
      </Grid>
    </>
  );
};

export const DeliverySettings = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    criticalSettings: {
      criticalAlertsEnabled,
      criticalAlertsStatus,
      timeSensitiveEnabled,
    },
    isIOS,
    isPushEnabled,
  } = useNotificationsSettings({
    regionSettingsQueryEnabled: false,
  });

  useEffect(() => {
    if (isIOS && criticalAlertsStatus === 'notSupported') {
      // They have not yet been prompted to enable critical alerts. Do that now so that the links to enable
      // critical alerts will show critical alerts as an option when taken to the app notification settings
      requestCriticalAlertsPermission();
    }
  }, [isIOS, criticalAlertsStatus]);

  return (
    <>
      <Typography variant="h3">{t('notifications.delivery.header')}</Typography>
      <Grid alignItems="center" container spacing={1}>
        {/* Notifications section */}
        <Grid size={1} sx={{ mt: 0.5 }}>
          <NotificationsActive color="secondary" />
        </Grid>
        <Grid size={7}>
          <Typography fontWeight="fontWeightMedium" variant="body1">
            {t('notifications.delivery.notifications.header')}
          </Typography>
        </Grid>
        <Grid size={4} sx={{ pt: 0.25, textAlign: 'right' }}>
          <StatusIndicator isEnabled={isPushEnabled} />
        </Grid>
        {isPushEnabled ? null : (
          <DisabledContent notificationType="notifications" />
        )}

        {isIOS ? (
          <>
            {/* Time-sensitive section */}
            <Grid size={1} sx={{ mt: 0.5 }}>
              <WatchLater sx={{ color: 'brand.500' }} />
            </Grid>
            <Grid size={7}>
              <Typography fontWeight="fontWeightMedium" variant="body1">
                {t('notifications.delivery.timeSensitive.header')}
              </Typography>
            </Grid>
            <Grid size={4} sx={{ pt: 0.25, textAlign: 'right' }}>
              <StatusIndicator isEnabled={timeSensitiveEnabled} />
            </Grid>
            {timeSensitiveEnabled ? null : (
              <DisabledContent notificationType="timeSensitive" />
            )}

            {/* Critical alerts section */}
            <Grid size={1} sx={{ mt: 0.5 }}>
              <WarningRounded color="error" />
            </Grid>
            <Grid size={7}>
              <Typography fontWeight="fontWeightMedium" variant="body1">
                {t('notifications.delivery.critical.header')}
              </Typography>
            </Grid>
            <Grid size={4} sx={{ pt: 0.25, textAlign: 'right' }}>
              <StatusIndicator isEnabled={criticalAlertsEnabled} />
            </Grid>
            {criticalAlertsEnabled ? null : (
              <DisabledContent notificationType="critical" />
            )}
          </>
        ) : null}
      </Grid>
    </>
  );
};
